import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import authService from '../services/authService';
import MyCasesCalendar from './MyCasesCalendar';
import ClosedCasesCalendar from './ClosedCasesCalendar';
import NewCase from './NewCase';
import NewMeeting from './NewMeeting';
import MyMeetings from './MyMeetings';
import Transactions from './Transactions';
import ManageTeams from './ManageTeams';
import Requests from './Requests';
import EditProfile from './EditProfile';
import AppLayout from './AppLayout';
import DirectConversations from './DirectConversations';

const Dashboard = ({ userRole, phoneNumber, userData: initialUserData, token, onLogout }) => {
  const [activeTab, setActiveTab] = useState('home');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [userData, setUserData] = useState(initialUserData);

  // Update local state when prop changes
  useEffect(() => {
    setUserData(initialUserData);
  }, [initialUserData]);

  // Handler for profile updates
  const handleProfileUpdate = (updatedData) => {
    setUserData(prev => ({
      ...prev,
      ...updatedData
    }));
  };

  // Called by ManageTeams when a new team is created
  const handleTeamCreated = () => {
    // Team created — chat is accessible via the Teams section
  };

  const handleLogoutClick = async () => {
    try {
      setIsLoggingOut(true);
      console.log('Logging out user with phone:', phoneNumber);
      
      // Call logout API
      await authService.logout(phoneNumber);
      console.log('Logout API call successful');
      
      // Call parent logout handler to reset app state
      onLogout();
    } catch (error) {
      console.error('Logout error:', error.message);
      // Still logout locally even if API fails
      onLogout();
    } finally {
      setIsLoggingOut(false);
    }
  };

  const menuItems = [
    {
      icon: '📁',
      title: 'My Cases',
      subtitle: 'View and manage cases',
      id: 'cases',
    },
    
    {
      icon: '➕',
      title: 'New Case',
      subtitle: 'Add a new case for a client',
      id: 'new-case',
    },
    {
      icon: '📅',
      title: 'My Meetings',
      subtitle: 'Meetings Scheduled with Clients',
      id: 'meetings',
    },
    {
      icon: '➕',
      title: 'New meeting',
      subtitle: 'Schedule a new meeting with Client',
      id: 'new-meeting',
    },
    {
      icon: '👥',
      title: 'My Teams',
      subtitle: 'View and manage your teams',
      id: 'teams',
    },
    {
      icon: '�',
      title: 'Transactions',
      subtitle: 'View and manage transactions',
      id: 'transactions',
    },
    {
      icon: '✅',
      title: 'Closed Cases',
      subtitle: 'View closed cases',
      id: 'closed-cases',
    },
  ];

  return (
    <AppLayout activeTab={activeTab} setActiveTab={setActiveTab}>
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="logo-section">
            <div className="logo">
              <span className="logo-icon">⚖️</span>
              <span className="logo-text">INSAFAPP</span>
            </div>
          </div>
          <div className="header-actions">
            <button 
              className="logout-button" 
              onClick={handleLogoutClick}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? 'Logging out…' : 'Logout'}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="dashboard-content">
          {activeTab === 'home' && (
            <div className="home-view">
              <div className="menu-grid">
                {menuItems.map(item => (
                  <div
                    key={item.id}
                    className="menu-card"
                    onClick={() => setActiveTab(item.id)}
                  >
                    <div className="menu-icon">{item.icon}</div>
                    <div className="menu-info">
                      <h3 className="menu-title">{item.title}</h3>
                      <p className="menu-subtitle">{item.subtitle}</p>
                    </div>
                    <span className="menu-card-arrow">›</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'cases' && (
            <MyCasesCalendar onBack={() => setActiveTab('home')} token={token} />
          )}

          {activeTab === 'closed-cases' && (
            <ClosedCasesCalendar onBack={() => setActiveTab('home')} token={token} />
          )}

          {activeTab === 'new-case' && (
            <NewCase onBack={() => setActiveTab('home')} token={token} />
          )}

          {activeTab === 'meetings' && (
            <MyMeetings onBack={() => setActiveTab('home')} token={token} />
          )}

          {activeTab === 'new-meeting' && (
            <NewMeeting onBack={() => setActiveTab('home')} token={token} />
          )}

          {activeTab === 'teams' && (
            <ManageTeams
              onBack={() => setActiveTab('home')}
              token={token}
              userData={userData}
              onTeamCreated={handleTeamCreated}
            />
          )}

          {activeTab === 'transactions' && (
            <Transactions onBack={() => setActiveTab('home')} token={token} />
          )}

          {activeTab === 'requests' && (
            <Requests onBack={() => setActiveTab('home')} token={token} />
          )}

          {activeTab === 'direct-chat' && (
            <DirectConversations
              token={token}
              userData={userData}
            />
          )}

          {activeTab === 'edit-profile' && (
            <EditProfile 
              onBack={() => setActiveTab('profile')} 
              token={token} 
              userData={userData}
              onProfileUpdate={handleProfileUpdate}
            />
          )}

          {activeTab === 'profile' && (
            <div className="view-section profile-view">
              <h2>My Profile</h2>
              {userData ? (
                <div className="profile-card">
                  <div className="profile-header">
                    {userData.profile_image && (
                      <img 
                        src={userData.profile_image.startsWith('http') ? userData.profile_image : `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/${userData.profile_image}`} 
                        alt="Profile" 
                        className="profile-image"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    {!userData.profile_image && (
                      <div className="profile-image-placeholder">👤</div>
                    )}
                    <div className="profile-name">
                      <h3>{userData.first_name} {userData.last_name}</h3>
                      <p className="profile-role">{userData.role === 'lawyer' ? '⚖️ Lawyer' : '👤 Guest'}</p>
                    </div>
                  </div>

                  <div className="profile-details">
                    <div className="profile-section">
                      <h4>Contact Information</h4>
                      <div className="profile-item">
                        <label>Phone Number:</label>
                        <p>{userData.mobile_number}</p>
                      </div>
                    </div>

                    {userData.role === 'lawyer' && (
                      <>
                        <div className="profile-section">
                          <h4>Location</h4>
                          <div className="profile-item">
                            <label>Country:</label>
                            <p>{userData.country}</p>
                          </div>
                          <div className="profile-item">
                            <label>State:</label>
                            <p>{userData.state}</p>
                          </div>
                          <div className="profile-item">
                            <label>City:</label>
                            <p>{userData.city}</p>
                          </div>
                        </div>

                        <div className="profile-section">
                          <h4>Professional Information</h4>
                          <div className="profile-item">
                            <label>License Number:</label>
                            <p>{userData.license_no}</p>
                          </div>
                          <div className="profile-item">
                            <label>Consultation Fee:</label>
                            <p>Rs. {userData.consultation_fee}</p>
                          </div>
                          <div className="profile-item">
                            <label>Practice Areas:</label>
                            <p>{userData.lawyer_practice}</p>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="profile-section">
                      <h4>Account Information</h4>
                      <div className="profile-item">
                        <label>Status:</label>
                        <p>{userData.status === 0 ? 'Active' : 'Inactive'}</p>
                      </div>
                      <div className="profile-item">
                        <label>Member Since:</label>
                        <p>{new Date(userData.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>

                  <div className="profile-actions">
                    <button 
                      className="edit-profile-button"
                      onClick={() => setActiveTab('edit-profile')}
                    >
                      ✏️ Edit Profile
                    </button>
                    <button 
                      className="back-button"
                      onClick={() => setActiveTab('home')}
                    >
                      ← Back
                    </button>
                  </div>
                </div>
              ) : (
                <div className="no-data">
                  <p>Profile data not available. Please log in again.</p>
                  <button 
                    className="back-button"
                    onClick={() => setActiveTab('home')}
                  >
                    ← Back
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default Dashboard;
